import { useState } from 'react';
import { Box, Chip, CircularProgress, IconButton, Typography } from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslate } from 'react-admin';
import type { MessageAttachment as MessageAttachmentType, AttachmentFileType } from '../../types/chat.types';
import { useMessageAttachmentUrl } from './hooks/useMessageAttachmentUrl';
import { MediaViewer } from './MediaViewer';
import { StickerPlayer } from './StickerPlayer';

interface MessageAttachmentProps {
  attachment: MessageAttachmentType;
}

export function MessageAttachment({ attachment }: MessageAttachmentProps) {
  const translate = useTranslate();
  const { url, loading } = useMessageAttachmentUrl(attachment);
  const [viewerOpen, setViewerOpen] = useState(false);

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!url) {
    return (
      <Chip
        icon={<InsertDriveFileIcon />}
        label={attachment.fileName || translate('resources.chat.fileNotAvailable')}
        variant="outlined"
        size="small"
        disabled
      />
    );
  }

  const isSticker = attachment.fileType === 'sticker';

  // Sticker attachment - uses StickerPlayer for animated (TGS), video (WebM), and static (WebP)
  if (isSticker) {
    const stickerSize = 150;

    return (
      <Box sx={{ display: 'block', width: stickerSize, height: stickerSize }}>
        <StickerPlayer
          url={url}
          mimeType={attachment.mimeType}
          size={stickerSize}
        />
      </Box>
    );
  }

  // GIF/Animation attachment — inline autoplay video, similar to sticker
  if (attachment.fileType === 'gif') {
    const gifSize = 200;

    return (
      <Box
        sx={{
          display: 'block',
          width: gifSize,
          height: gifSize,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <video
          src={url}
          autoPlay
          loop
          muted
          playsInline
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
        {attachment.caption && (
          <Box
            sx={{
              mt: 0.5,
              fontSize: '0.875rem',
              color: 'text.secondary',
            }}
          >
            {attachment.caption}
          </Box>
        )}
      </Box>
    );
  }

  // Check if this is an image-like attachment (image documents, etc.)
  const isImageLike =
    attachment.fileType === 'image' ||
    attachment.mimeType?.startsWith('image/');

  // Image/GIF/WebP attachment - Display as fixed-size thumbnail like WhatsApp
  // Users click to view full size
  if (isImageLike) {
    const thumbnailSize = 200;

    return (
      <>
        <Box
          sx={{
            position: 'relative',
            display: 'block',
            width: thumbnailSize,
            height: thumbnailSize,
            '&:hover .download-button': {
              opacity: 1,
            },
          }}
        >
          <Box
            component="img"
            src={url}
            alt={attachment.fileName || translate('resources.chat.image')}
            sx={{
              display: 'block',
              width: '100%',
              height: '100%',
              borderRadius: 2,
              cursor: 'pointer',
              objectFit: 'cover',
              bgcolor: 'action.hover',
            }}
            onClick={() => setViewerOpen(true)}
            loading="lazy"
          />
          <IconButton
            className="download-button"
            size="small"
            component="a"
            href={url}
            download={attachment.fileName || 'image'}
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              opacity: 0,
              transition: 'opacity 0.2s',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.7)',
              },
            }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
          {attachment.caption && (
            <Box
              sx={{
                mt: 0.5,
                fontSize: '0.875rem',
                color: 'text.secondary',
              }}
            >
              {attachment.caption}
            </Box>
          )}
        </Box>
        <MediaViewer
          open={viewerOpen}
          onClose={() => setViewerOpen(false)}
          url={url}
          fileName={attachment.fileName}
          fileType="image"
          caption={attachment.caption}
        />
      </>
    );
  }

  // Audio attachment
  if (attachment.fileType === 'audio') {
    return (
      <Box sx={{ maxWidth: '100%' }}>
        <audio controls src={url} style={{ maxWidth: '100%' }}>
          {translate('resources.chat.audioNotSupported')}
        </audio>
      </Box>
    );
  }

  // Video attachment - Fixed size thumbnail like WhatsApp
  if (attachment.fileType === 'video') {
    const thumbnailSize = 200;

    return (
      <>
        <Box
          sx={{
            display: 'block',
            width: thumbnailSize,
            height: thumbnailSize,
            position: 'relative',
            cursor: 'pointer',
            bgcolor: 'grey.900', // Dark background for video
            borderRadius: 2,
            overflow: 'hidden',
          }}
          onClick={() => setViewerOpen(true)}
        >
          <video
            src={url}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          >
            {translate('resources.chat.videoNotSupported')}
          </video>
          {attachment.caption && (
            <Box
              sx={{
                mt: 0.5,
                fontSize: '0.875rem',
                color: 'text.secondary',
              }}
            >
              {attachment.caption}
            </Box>
          )}
        </Box>
        <MediaViewer
          open={viewerOpen}
          onClose={() => setViewerOpen(false)}
          url={url}
          fileName={attachment.fileName}
          fileType="video"
          caption={attachment.caption}
        />
      </>
    );
  }

  // Check if it's a PDF
  const isPdf =
    attachment.mimeType === 'application/pdf' ||
    attachment.fileName?.toLowerCase().endsWith('.pdf');

  // PDF attachment - Rich preview card (WhatsApp/Telegram-style)
  if (attachment.fileType === 'document' && isPdf) {
    const fileName = attachment.fileName || `${translate('resources.chat.preview.document')}.pdf`;
    const fileSizeLabel = attachment.fileSize
      ? attachment.fileSize >= 1024 * 1024
        ? `${(attachment.fileSize / (1024 * 1024)).toFixed(1)} MB`
        : `${Math.round(attachment.fileSize / 1024)} KB`
      : null;

    return (
      <>
        <Box
          onClick={() => setViewerOpen(true)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            width: 260,
            maxWidth: '100%',
            bgcolor: 'action.hover',
            borderRadius: 2,
            cursor: 'pointer',
            border: '1px solid',
            borderColor: 'divider',
            transition: 'background-color 0.15s',
            '&:hover': {
              bgcolor: 'action.selected',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 42,
              height: 42,
              flexShrink: 0,
              borderRadius: 1.5,
              bgcolor: '#e53935',
            }}
          >
            <PictureAsPdfIcon sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              noWrap
              sx={{ fontWeight: 500, lineHeight: 1.3 }}
            >
              {fileName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {[fileSizeLabel, 'PDF'].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
          <IconButton
            size="small"
            component="a"
            href={url}
            download={fileName}
            onClick={(e) => e.stopPropagation()}
            sx={{
              flexShrink: 0,
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' },
            }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Box>
        {attachment.caption && (
          <Typography
            variant="body2"
            sx={{ mt: 0.5, color: 'text.secondary', fontSize: '0.875rem' }}
          >
            {attachment.caption}
          </Typography>
        )}
        <MediaViewer
          open={viewerOpen}
          onClose={() => setViewerOpen(false)}
          url={url}
          fileName={attachment.fileName}
          fileType="pdf"
        />
      </>
    );
  }

  // Other file types (document, etc.)
  return (
    <Chip
      icon={<InsertDriveFileIcon />}
      label={attachment.fileName || translate('resources.chat.file')}
      variant="outlined"
      size="small"
      clickable
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
    />
  );
}
